import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMapEvents, useMap } from "react-leaflet";
/* Isti moduli koje koristi i GPS mapa — ne kopija.

   Radni nalog i praćenje flote gledaju iste puteve i istu vrstu događaja. Dve
   odvojene liste vrsta ili dve palete boja značile bi da ista blokada na dva
   ekrana izgleda različito, a dispečer prelazi između njih u istom poslu. */
import { SLOJEVI, nadjiSloj, imaSaobracaj } from "../GPS/slojeviMape";
import { PULS_NAJVISE, TRAGOVI_DEONICE, veziOblacic, ikonaIncidenta, vrstaOpis, kasnjenjeTeksta } from "../GPS/incidenti";
import PopupIncident from "../GPS/PopupIncident";
/* Izgled oznaka i kartice incidenta živi u GPS.css. Uvozi se OVDE izričito, a ne
   računa se na to što GPS ekran ionako ulazi u isti paket — kad se GPS jednom
   bude učitavao odvojeno, ovaj ekran bi ostao sa oznakama bez stila.
   Webpack isti fajl uključuje jednom, pa dvostruki uvoz ništa ne košta. */
import "../GPS/GPS.css";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import icon from "leaflet/dist/images/marker-icon.png";
import icon2x from "leaflet/dist/images/marker-icon-2x.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import CustomDropdown from "../../custom/customDropdown";

// CRA ne resolvuje default marker putanje sam — slike idu iz leaflet paketa (bez CDN-a).
L.Icon.Default.mergeOptions({
    iconUrl: icon,
    iconRetinaUrl: icon2x,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
});

const CENTAR_SRBIJA = [44.7866, 20.4489];

/* Google Maps URLs — deep link u samu Google aplikaciju. Ne traži API ključ i
   ne naplaćuje se, a vozač dobija pravu navigaciju sa saobraćajem uživo.
   Uslovi korišćenja su ispoštovani jer se Google sadržaj prikazuje u njegovoj
   aplikaciji, a ne na našoj Leaflet mapi. */
const GOOGLE_DIR = "https://www.google.com/maps/dir/";
const MAX_MEDJUTACAKA = 9;   // ograničenje Maps URLs-a (u mobilnom pregledaču čak 3)

/* Izgled tačke po tipu. Tipovi dolaze iz modgena pa im se id ne zna unapred —
   prepoznaju se po rednom mestu u grupi, isto kao u proceduri. */
const IZGLED = [
    { boja: "#2e7d32", ikona: "pi-download" },      // 1 Utovar
    { boja: "#c62828", ikona: "pi-upload" },        // 2 Istovar
    { boja: "#ef6c00", ikona: "pi-pause" },         // 3 Pauza
    { boja: "#6a1b9a", ikona: "pi-flag" },          // 4 Granični prelaz
    { boja: "#0277bd", ikona: "pi-map-marker" },    // 5 Usputna tačka
];
const IZGLED_NEPOZNAT = { boja: "#546e7a", ikona: "pi-map-marker" };

/* Oznaka rezultata pretrage — samo pokazuje gde je nadjeno mesto.
   Namerno drugacija od pinova rute i NE ulazi u tacke koje se rutiraju. */
const ikonaPretrage = () => L.divIcon({
    className: "ruta-nalaz-wrap",
    html: `<span class="ruta-nalaz"><i class="pi pi-search"></i></span>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
});

// Pin sa brojem i simbolom tipa — oblik kaplje, kao klasična oznaka na mapi.
const pinIkona = (broj, izgled) => L.divIcon({
    className: "ruta-pin-wrap",
    html: `
        <div class="ruta-pin" style="--pin: ${izgled.boja}">
            <span class="ruta-pin-broj">${broj}</span>
            <i class="pi ${izgled.ikona} ruta-pin-ikona"></i>
        </div>`,
    iconSize: [34, 44],
    iconAnchor: [17, 44],
});

/* Ručka oblikovanja — sitan beo krug, namerno neupadljiv i bez broja: to nije
   stajalište nego samo tačka kroz koju putanja mora da prođe. */
const rucicaIkona = (uToku = false) => L.divIcon({
    className: "ruta-rucica-wrap",
    html: `<span class="ruta-rucica${uToku ? " u-toku" : ""}"></span>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
});

/* Ručni unos koordinata.
   Tekst se drži lokalno da bi se moglo slobodno kucati — međustanja poput "44."
   ili "-" nisu broj, pa bi upisivanje pravo u tačku obrisalo marker sa mape.
   Gore se šalje tek ispravna vrednost. */
const KoordinateUnos = ({ lat, lon, onPromeni }) => {
    const { t } = useTranslation();
    const [tekst, setTekst] = useState({ lat: "", lon: "" });
    const [uFokusu, setUFokusu] = useState(false);

    /* Dok korisnik kuca, spoljna vrednost ne sme da mu prepisuje polje; kad
       marker odvuče po mapi (a polje nije u fokusu), brojevi se osveže. */
    useEffect(() => {
        if (uFokusu) return;
        setTekst({
            lat: lat != null ? Number(lat).toFixed(6) : "",
            lon: lon != null ? Number(lon).toFixed(6) : "",
        });
    }, [lat, lon, uFokusu]);

    const ispravan = (polje, vrednost) => {
        const broj = Number(vrednost);
        if (vrednost.trim() === "" || Number.isNaN(broj)) return null;
        return Math.abs(broj) <= (polje === "lat" ? 90 : 180) ? broj : null;
    };

    const promeni = (polje, uneto) => {
        /* Nalepljen par "44.96854, 20.22907" popunjava oba polja odjednom —
           tako se koordinate najčešće i kopiraju sa mapa.
           Oba broja MORAJU imati tačku, inače bi "44,5" (decimalni zarez, koji
           je kod nas uobičajen) bio pogrešno pročitan kao par 44 i 5. */
        const par = /^\s*(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)\s*$/.exec(uneto);
        if (par) {
            const a = ispravan("lat", par[1]);
            const b = ispravan("lon", par[2]);
            if (a != null && b != null) {
                setTekst({ lat: par[1], lon: par[2] });
                onPromeni(a, b);
                return;
            }
        }

        /* zarez kao decimalni znak — uobičajen na našim tastaturama */
        const ociscen = uneto.replace(",", ".");
        setTekst(prev => ({ ...prev, [polje]: ociscen }));

        const broj = ispravan(polje, ociscen);
        if (broj == null) return;
        onPromeni(
            polje === "lat" ? broj : Number(tekst.lat),
            polje === "lon" ? broj : Number(tekst.lon)
        );
    };

    const greska = (polje) => tekst[polje] !== "" && ispravan(polje, tekst[polje]) == null;

    return (
        <div className="ruta-koord-unos">
            <span className="ruta-koord-oznaka">
                <i className="pi pi-map-marker" /> {t("ruta.koordinate")}
            </span>
            <InputText
                value={tekst.lat}
                onFocus={() => setUFokusu(true)}
                onBlur={() => setUFokusu(false)}
                onChange={(e) => promeni("lat", e.target.value)}
                className={greska("lat") ? "p-invalid" : ""}
                placeholder="Lat"
                title={greska("lat") ? t("ruta.neispravnaKoordinata") : undefined}
            />
            <InputText
                value={tekst.lon}
                onFocus={() => setUFokusu(true)}
                onBlur={() => setUFokusu(false)}
                onChange={(e) => promeni("lon", e.target.value)}
                className={greska("lon") ? "p-invalid" : ""}
                placeholder="Lon"
                title={greska("lon") ? t("ruta.neispravnaKoordinata") : undefined}
            />
        </div>
    );
};

/* Jedan klik dodaje tačku, dvoklik uvećava mapu.
   Leaflet pri dvokliku prvo ispali DVA click događaja, pa tek onda dblclick —
   zato se dodavanje odlaže i otkazuje ako dvoklik stigne. Bez toga bi svako
   uvećavanje ostavilo dve tačke za sobom. */
const KlikNaMapu = ({ aktivno, onKlik }) => {
    const tajmer = useRef(null);

    useMapEvents({
        click: (e) => {
            if (!aktivno) return;
            const { lat, lng } = e.latlng;
            clearTimeout(tajmer.current);
            tajmer.current = setTimeout(() => onKlik(lat, lng), 260);
        },
        dblclick: () => clearTimeout(tajmer.current),
    });

    useEffect(() => () => clearTimeout(tajmer.current), []);
    return null;
};

// Kad se skup tačaka promeni, mapa se sama namesti da ih sve obuhvati.
const UokviriTacke = ({ tacke }) => {
    const map = useMap();
    useEffect(() => {
        const sa = tacke.filter(t => t.lat != null && t.lon != null);
        if (!sa.length) return;
        if (sa.length === 1) { map.setView([sa[0].lat, sa[0].lon], 11); return; }
        map.fitBounds(L.latLngBounds(sa.map(t => [t.lat, t.lon])), { padding: [40, 40] });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tacke.length]);
    return null;
};

/* Povlačenje linije — kao na velikim mapama: uhvatiš deonicu, odvučeš je na
   drugi put i tu nastaje nova usputna tačka kroz koju ruta mora da prođe.
   Tako operater može da izbegne putarinu ili izabere bezbedniju varijantu,
   a ne samo najkraću. */
const PovlacenjeLinije = ({ vucenje, setVucenje, onSpusti }) => {
    const map = useMap();

    useMapEvents({
        mousemove: (e) => {
            if (!vucenje) return;
            setVucenje(v => v && ({ ...v, pozicija: [e.latlng.lat, e.latlng.lng] }));
        },
        mouseup: (e) => {
            if (!vucenje) return;
            map.dragging.enable();
            onSpusti(vucenje.deo, e.latlng.lat, e.latlng.lng);
            setVucenje(null);
        },
    });

    /* Dok se vuče, mapa ne sme da se pomera pod prstom */
    useEffect(() => {
        if (vucenje) map.dragging.disable();
        return () => { map.dragging.enable(); };
    }, [vucenje, map]);

    return null;
};

/* Pomeranje mape na izabrani rezultat pretrage */
const IdiNa = ({ cilj }) => {
    const map = useMap();
    useEffect(() => {
        if (cilj) map.flyTo([cilj.lat, cilj.lon], 14);
    }, [cilj, map]);
    return null;
};

const RutaVozaca = ({ idWorkLog, token, toast }) => {
    const { t, i18n } = useTranslation();

    const [otvoren, setOtvoren] = useState(false);
    const [tacke, setTacke] = useState([]);
    const [predlog, setPredlog] = useState([]);
    const [tipoviTacke, setTipoviTacke] = useState([]);
    const [vozaciDB, setVozaciDB] = useState([]);
    const [vozilaDB, setVozilaDB] = useState([]);
    const [rutaVozac, setRutaVozac] = useState(null);
    const [rutaVozilo, setRutaVozilo] = useState(null);
    const [nalog, setNalog] = useState(null);
    const [delovi, setDelovi] = useState([]);
    const [sumarno, setSumarno] = useState(null);
    const [profil, setProfil] = useState(null);   // KAMION | AUTO
    /* Pretraga mesta — kao polje za pretragu na velikim mapama */
    const [pretraga, setPretraga] = useState("");
    const [rezultati, setRezultati] = useState([]);
    const [trazi, setTrazi] = useState(false);
    const [cilj, setCilj] = useState(null);
    const [ucitava, setUcitava] = useState(false);
    const [racuna, setRacuna] = useState(false);
    const [vucenje, setVucenje] = useState(null);
    const [izbegavajPutarine, setIzbegavajPutarine] = useState(false);
    const [linkKopiran, setLinkKopiran] = useState(false);

    /* ---- saobraćaj na zadatoj trasi ----
       Podloga i sloj gustine su NEZAVISNI: gustina je proziran sloj iznad bilo
       koje podloge, pa se satelit i saobraćaj mogu gledati zajedno. */
    const [sloj, setSloj] = useState(SLOJEVI[0].kljuc);
    const [izbornikSlojeva, setIzbornikSlojeva] = useState(false);
    const [plocica, setPlocica] = useState(null);
    const [prikaziSaobracaj, setPrikaziSaobracaj] = useState(false);
    const [prepreke, setPrepreke] = useState([]);
    const [saSaobracajem, setSaSaobracajem] = useState(null);
    const [prepreekeUcitava, setPreprekeUcitava] = useState(false);
    /* Kartica se pravi samo za kliknutu oznaku. Kačiti je na svaku znači
       iscrtati je za svaku — React-Leaflet iscrtava decu popupa i kad je
       zatvoren. (Isto je koštalo i GPS mapu, vidi OP-81.) */
    const [otvorenaPrepreka, setOtvorenaPrepreka] = useState(null);
    const brojacPrepreke = useRef(0);
    const mapaRef = useRef(null);

    const poruka = useCallback((severity, summary, detail) => {
        toast?.current?.show({ severity, summary, detail, life: 3000 });
    }, [toast]);

    /* Redosled tipova u grupi određuje boju i simbol */
    const izgledZaTip = (tip) => {
        const i = tipoviTacke.findIndex(o => o.value === tip);
        return i >= 0 && i < IZGLED.length ? IZGLED[i] : IZGLED_NEPOZNAT;
    };

    /* ---- učitavanje ---- */
    useEffect(() => {
        if (!idWorkLog) {
            setTacke([]); setPredlog([]); setNalog(null); setDelovi([]); setSumarno(null);
            return;
        }
        let otkazano = false;
        setUcitava(true);

        fetch(process.env.REACT_APP_BACKEND_URL + "/trip/RadniNalogVozacInit", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
            body: JSON.stringify({ id_radni_nalog: idWorkLog }),
        })
            .then(r => r.json())
            .then(data => {
                if (otkazano) return;
                const n = (data.NalogDB || [])[0] || null;
                setNalog(n);
                setTipoviTacke(data.TipTackeDB || []);
                setPredlog(data.PredlogDB || []);
                setVozaciDB(data.VozaciDB || []);
                setVozilaDB(data.VozilaDB || []);
                setRutaVozac(n?.ruta_vozac ?? null);
                setRutaVozilo(n?.ruta_vozilo ?? null);
                setTacke((data.RutaDB || []).map(r => ({
                    tip_tacke: r.tip_tacke,
                    naziv: r.naziv || "",
                    oblikovanje: !!r.oblikovanje,
                    lat: r.lat != null ? Number(r.lat) : null,
                    lon: r.lon != null ? Number(r.lon) : null,
                    napomena: r.napomena || "",
                })));
                setDelovi([]);
                setSumarno(null);
                /* Konfiguraciju gradi server jer u sebi nosi ključ provajdera. */
                setPlocica(data.PlocicaSaobracaja || null);
            })
            .catch(() => { if (!otkazano) poruka("error", t("common.greska"), t("ruta.greskaUcitavanje")); })
            .finally(() => { if (!otkazano) setUcitava(false); });

        return () => { otkazano = true; };
    }, [idWorkLog, token, poruka, t]);

    /* ---- rad sa tačkama ----
       Nijedna izmena ne briše postojeću putanju: stara ostaje na mapi dok
       ruter ne vrati novu, pa nema treperenja pri svakom kliku. */
    /* Svaka nova tačka se odmah lepi na najbliži put kojim KAMION sme.
       Ručno dugme za to više ne postoji — teretni profil je jedini koji ovde
       ima smisla, pa nema razloga da operater na to misli. */
    const dodajTacku = async (lat, lon, tip) => {
        let k = { lat, lon };
        try {
            const [nl] = await nalepi([{ lat, lon }]);
            if (nl && nl.udaljenost != null) k = { lat: nl.lat, lon: nl.lon };
        } catch (e) { /* bez lepljenja ostaje gde je spuštena */ }

        setTacke(prev => [...prev, {
            tip_tacke: tip ?? (tipoviTacke[tipoviTacke.length - 1]?.value ?? null),
            naziv: "", lat: k.lat, lon: k.lon, napomena: "", oblikovanje: false,
        }]);
    };

    const izmeniTacku = (i, polje, vrednost) => {
        setTacke(prev => prev.map((t, idx) => idx === i ? { ...t, [polje]: vrednost } : t));
    };

    /* Novo stajalište na tačno određeno mesto u ruti (pauza između utovara i
       istovara i sl.). Postavlja se na sredinu između suseda, pa se markerom
       dovuče gde stvarno treba — bez toga bi operater morao da ga doda na kraj
       i onda ga strelicama vuče kroz celu listu. */
    const dodajStajaliste = (posleIndex) => {
        const tipPodrazumevani = tipoviTacke[tipoviTacke.length - 1]?.value ?? null;

        setTacke(prev => {
            const pre = prev[posleIndex];
            const posle = prev[posleIndex + 1];

            let lat, lon;
            if (pre && posle) {
                lat = (Number(pre.lat) + Number(posle.lat)) / 2;
                lon = (Number(pre.lon) + Number(posle.lon)) / 2;
            } else if (pre) {
                /* poslednja tačka — novo stajalište ide malo dalje da se pinovi ne poklope */
                lat = Number(pre.lat) + 0.05;
                lon = Number(pre.lon) + 0.05;
            } else {
                const centar = mapaRef.current?.getCenter();
                lat = centar ? centar.lat : CENTAR_SRBIJA[0];
                lon = centar ? centar.lng : CENTAR_SRBIJA[1];
            }

            const kopija = [...prev];
            kopija.splice(posleIndex + 1, 0, {
                tip_tacke: tipPodrazumevani,
                naziv: "",
                lat, lon,
                napomena: "",
                oblikovanje: false,
            });
            return kopija;
        });
    };

    const obrisiTacku = (i) => setTacke(prev => prev.filter((_, idx) => idx !== i));

    const pomeri = (i, smer) => {
        const j = i + smer;
        setTacke(prev => {
            if (j < 0 || j >= prev.length) return prev;
            const kopija = [...prev];
            [kopija[i], kopija[j]] = [kopija[j], kopija[i]];
            return kopija;
        });
    };

    const pretraziMesto = async () => {
        const upit = pretraga.trim();
        if (upit.length < 3) return;
        setTrazi(true);
        try {
            const r = await fetch(process.env.REACT_APP_BACKEND_URL + "/trip/TraziMesto", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
                body: JSON.stringify({ upit }),
            });
            const data = await r.json();
            setRezultati(data.rezultati || []);
            if (!(data.rezultati || []).length) {
                poruka("warn", t("common.upozorenje"), data.poruka || t("ruta.nemaRezultata"));
            }
        } catch (e) {
            poruka("error", t("common.greska"), t("ruta.greskaPretrage"));
        } finally {
            setTrazi(false);
        }
    };

    /* Lepljenje na putnu mrežu.
       Tačka spuštena van puta ruteru ne znači ništa — on je prilepi na najbliži
       put, ponekad kilometrima dalje i na neimenovanu stazu, pa ruta dobije
       izlomke koje operater ne može da objasni gledajući svoju tačku. */
    const UPOZORENJE_M = 300;

    const nalepi = async (tackeZaSlanje) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + "/trip/NalepiTacke", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
            body: JSON.stringify({ tacke: tackeZaSlanje.map(t => ({ lat: t.lat, lon: t.lon })) }),
        });
        const data = await r.json();
        return data.tacke || [];
    };


    /* Spuštanje povučene deonice pravi tačku OBLIKOVANJA, ne stajalište:
       ona samo provlači putanju drugim putem i vozač je ne vidi kao zadatak.
       Ako se tu zaista nešto radi (pokupiti papire), operater je jednim klikom
       pretvori u stajalište. */
    const ubaciOblikovanje = async (indexDela, lat, lon) => {
        /* Tačka oblikovanja služi samo da kaže KOJIM putem, pa se odmah lepi na
           najbliži put — inače ruter sam bira gde da je prilepi i ruta odluta. */
        let konacna = { lat, lon };
        try {
            const [n] = await nalepi([{ lat, lon }]);
            if (n && n.udaljenost != null) konacna = { lat: n.lat, lon: n.lon };
        } catch (e) { /* bez lepljenja ostaje tamo gde je spuštena */ }

        setTacke(prev => {
            const kopija = [...prev];
            kopija.splice(indexDela + 1, 0, {
                tip_tacke: null,
                naziv: "",
                lat: konacna.lat,
                lon: konacna.lon,
                napomena: "",
                oblikovanje: true,
            });
            return kopija;
        });
    };

    /* Oblikovanje → stajalište: dobija podrazumevani tip (usputna tačka) i
       ulazi u numerisanu listu koju vozač vidi. */
    const uStajaliste = (i) => {
        const tipUsputna = tipoviTacke[4]?.value ?? tipoviTacke[tipoviTacke.length - 1]?.value ?? null;
        setTacke(prev => prev.map((t, idx) =>
            idx === i ? { ...t, oblikovanje: false, tip_tacke: t.tip_tacke ?? tipUsputna } : t));
    };

    const obrisiSvaOblikovanja = () => setTacke(prev => prev.filter(t => !t.oblikovanje));

    /* Predlog iz utovara i istovara — adrese su već geokodirane na backendu.
       Tačke bez koordinata se preskaču, ali se broj javlja operateru da zna
       da neku adresu mora sam da postavi na mapi. */
    const ucitajPredlog = () => {
        const sa = predlog.filter(p => p.lat != null && p.lon != null);
        const bez = predlog.length - sa.length;

        if (!sa.length) {
            poruka("warn", t("common.upozorenje"), t("ruta.predlogPrazan"));
            return;
        }
        setTacke(sa.map(p => ({
            tip_tacke: p.tip_tacke,
            naziv: [p.naziv, p.grad].filter(Boolean).join(" — "),
            lat: Number(p.lat),
            lon: Number(p.lon),
            napomena: p.adresa || "",
        })));
        poruka(bez ? "warn" : "success", t("ruta.predlogNaslov"),
            bez ? t("ruta.predlogDelimican", { broj: bez }) : t("ruta.predlogUcitan"));
    };

    /* ---- putanja po putevima ----
       Ruta se računa sama, na svaku promenu tačaka: dodavanje, prevlačenje
       markera, promenu redosleda, brisanje ili povlačenje linije. Potpis skupa
       tačaka je ključ efekta, pa izmena naziva ili napomene ne pokreće poziv. */
    const potpisTacaka = tacke
        .filter(t => t.lat != null && t.lon != null)
        .map(t => `${Number(t.lat).toFixed(5)},${Number(t.lon).toFixed(5)}`)
        .join("|");

    useEffect(() => {
        const koord = potpisTacaka ? potpisTacaka.split("|") : [];
        if (koord.length < 2) {
            setDelovi([]);
            setSumarno(null);
            return;
        }

        /* Kratko odlaganje spaja niz brzih izmena u jedan poziv rutera. */
        const kontrola = new AbortController();
        const tajmer = setTimeout(() => {
            setRacuna(true);
            fetch(process.env.REACT_APP_BACKEND_URL + "/trip/RutaGeometrija", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
                body: JSON.stringify({
                    tacke: koord.map(par => {
                        const [lat, lon] = par.split(",");
                        return { lat: Number(lat), lon: Number(lon) };
                    }),
                    /* vozilo određuje kamionska ograničenja; server ih čita iz
                       baze, ovde ide samo koje je vozilo izabrano */
                    id_vozilo: rutaVozilo,
                    id_prikljucno: nalog?.id_prikljucno ?? null,
                    /* Opasan teret server cita sam iz utovara naloga — kakav se teret
                       vozi je poslovni podatak i ne sme da zavisi od pregledaca. */
                    id_radni_nalog: idWorkLog,
                    izbegavajPutarine,
                }),
                signal: kontrola.signal,
            })
                .then(r => r.json())
                .then(data => {
                    setDelovi(data.delovi || []);
                    setSumarno(data.delovi?.length ? { km: data.km, minuta: data.minuta } : null);
                    setProfil(data.delovi?.length
                        ? { vrsta: data.profil, ogranicenja: data.ogranicenja, masaZanemarena: data.masaZanemarena, adr: !!data.adr }
                        : null);
                    if (data.poruka) poruka("warn", t("common.upozorenje"), data.poruka);
                })
                .catch(e => {
                    if (e.name === "AbortError") return;
                    setDelovi([]);
                    setSumarno(null);
                    setProfil(null);
                })
                .finally(() => setRacuna(false));
        }, 500);

        return () => { clearTimeout(tajmer); kontrola.abort(); };
    }, [potpisTacaka, token, rutaVozilo, izbegavajPutarine, nalog, poruka, t]);

    /* ---- šta čeka na trasi ----

       Traži se TEK KAD RUTA POSTOJI i nad njenom stvarnom geometrijom, ne nad
       okvirom oko tačaka: ruta Beograd—Zagreb prolazi kroz uzak koridor, a okvir
       oko njenih krajeva obuhvatio bi pola Hrvatske i pola Srbije i javljao radove
       kojima vozač nikad neće proći.

       Odvojeno od računanja rute, a ne u istom pozivu: ruta se menja na svaki
       pomeraj tačke i mora da bude brza, a ovo je upit ka spoljnom servisu koji
       traje i ume da zakaže. Kad zakaže, ruta ostaje — samo se ne zna šta je na
       njoj. */
    const potpisTrase = useMemo(
        () => delovi.map(d => (d.geometrija || []).length).join('-') + '|' + (delovi.length ? 1 : 0),
        [delovi]
    );

    useEffect(() => {
        const trasa = delovi.flatMap(d => d.geometrija || []);
        if (trasa.length < 2) { setPrepreke([]); setSaSaobracajem(null); return undefined; }

        const kontrola = new AbortController();
        setPreprekeUcitava(true);

        const tajmer = setTimeout(() => {
            const koord = potpisTacaka ? potpisTacaka.split("|") : [];
            fetch(process.env.REACT_APP_BACKEND_URL + "/trip/RutaSaobracaj", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
                body: JSON.stringify({
                    trasa,
                    tacke: koord.map(par => {
                        const [lat, lon] = par.split(",");
                        return { lat: Number(lat), lon: Number(lon) };
                    }),
                    id_vozilo: rutaVozilo,
                    id_prikljucno: nalog?.id_prikljucno ?? null,
                    id_radni_nalog: idWorkLog,
                    izbegavajPutarine,
                    jezik: i18n.language,
                }),
                signal: kontrola.signal,
            })
                .then(r => r.json())
                .then(d => {
                    setPrepreke(d.incidenti || []);
                    setSaSaobracajem(d.saSaobracajem || null);
                })
                .catch(e => {
                    if (e.name === "AbortError") return;
                    setPrepreke([]); setSaSaobracajem(null);
                })
                .finally(() => setPreprekeUcitava(false));
        }, 700);

        return () => { clearTimeout(tajmer); kontrola.abort(); setPreprekeUcitava(false); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [potpisTrase, potpisTacaka, token, rutaVozilo, izbegavajPutarine, nalog, idWorkLog, i18n.language]);

    /* Pulsira se samo dok je zatvorenih puteva malo — iznad toga prsten koji
       pulsira ne ističe ništa, a svaka animacija je sloj koji se crta stalno. */
    const pulsirajuBlokade = useMemo(
        () => prepreke.filter(x => x.zatvoren).length <= PULS_NAJVISE,
        [prepreke]
    );

    const sazetakPrepreka = useMemo(() => ({
        ukupno: prepreke.length,
        zatvorenih: prepreke.filter(x => x.zatvoren).length,
        radova: prepreke.filter(x => x.vrsta === "RADOVI").length,
    }), [prepreke]);

    const aktivnaPodloga = useMemo(() => nadjiSloj(sloj), [sloj]);

    /* ---- snimanje ---- */
    const sacuvaj = () => {
        if (!idWorkLog) {
            poruka("warn", t("common.upozorenje"), t("ruta.nemaNaloga"));
            return;
        }
        const zaSnimanje = tacke
            .filter(t => t.lat != null && t.lon != null)
            .map((t, i) => ({
                redosled: i + 1,
                tip_tacke: t.tip_tacke ?? null,
                naziv: t.naziv || null,
                lat: t.lat,
                lon: t.lon,
                napomena: t.napomena || null,
                oblikovanje: t.oblikovanje ? 1 : 0,
            }));

        /* Uz tačke se snima i SAMA PUTANJA koju je izračunao ruter, plus njena
           kilometraža. Bez toga se izveštaj "ko nije poštovao rutu" ne može
           uraditi: odstupanje se mora meriti od stvarne putanje, a ne od prave
           linije između utovara i istovara (Zagreb je 69 km od prave Beograd–
           Milano, a nalazi se tačno na putu). Ranije se ovo samo prikazivalo
           pa bacalo pri snimanju. */
        const putanja = delovi.flatMap(d => d.geometrija || []);

        /* Ruter ume da vrati desetine hiljada tačaka. Za merenje udaljenosti od
           linije to nije potrebno — oblik se čuva i sa proređenim nizom, a
           zahtev ostaje razumne veličine. Prva i poslednja se uvek zadržavaju. */
        const PROREDI_NA = 3000;
        const korak = Math.ceil(putanja.length / PROREDI_NA) || 1;
        const putanjaZaSnimanje = korak === 1
            ? putanja
            : [...putanja.filter((_, i) => i % korak === 0), putanja[putanja.length - 1]].filter(Boolean);

        fetch(process.env.REACT_APP_BACKEND_URL + "/trip/ManageRadniNalogVozac", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
            body: JSON.stringify({
                step: "SAVE_RUTA",
                id_radni_nalog: idWorkLog,
                tacke: zaSnimanje,
                ruta_vozac: rutaVozac,
                ruta_vozilo: rutaVozilo,
                // koordinate idu kao [lon, lat], isto kako ih ruter i vraća
                putanja: putanjaZaSnimanje,
                planirano_km: sumarno?.km ?? null,
                planirano_minuta: sumarno?.minuta ?? null,
            }),
        })
            .then(r => r.json())
            .then(data => {
                const red = Array.isArray(data) ? data[0] : data;
                poruka("success", t("ruta.naslov"), red?.message || t("ruta.sacuvana"));
            })
            .catch(() => poruka("error", t("common.greska"), t("ruta.greskaSnimanje")));
    };

    const saKoordinatama = tacke.filter(t => t.lat != null && t.lon != null);
    const praveLinije = saKoordinatama.map(t => [t.lat, t.lon]);

    /* Numeracija ide samo po stajalištima — tačke oblikovanja nisu zadatak i ne
       ulaze u redosled koji vozač vidi. */
    const brojStajalista = (indexUSvim) =>
        tacke.slice(0, indexUSvim + 1).filter(t => !t.oblikovanje).length;

    const stajalista = tacke.filter(t => !t.oblikovanje);
    const brojOblikovanja = tacke.filter(t => t.oblikovanje).length;

    /* ---- Google Maps link ----
       U link ulaze SVE tačke, i oblikovanje. Ako je dispečer rutu namerno
       provukao drugim putem — zbog putarine, zabrane ili bezbednosti — vozač
       mora da dobije baš tu trasu, inače oblikovanje nema svrhe. */
    const zaNavigaciju = tacke.filter(t => t.lat != null && t.lon != null);

    const medjutacke = zaNavigaciju.slice(1, -1);
    const izostavljeno = Math.max(0, medjutacke.length - MAX_MEDJUTACAKA);

    const googleLink = useMemo(() => {
        if (zaNavigaciju.length < 2) return null;

        const kord = (t) => `${Number(t.lat).toFixed(6)},${Number(t.lon).toFixed(6)}`;
        const parametri = new URLSearchParams({
            api: "1",
            origin: kord(zaNavigaciju[0]),
            destination: kord(zaNavigaciju[zaNavigaciju.length - 1]),
            travelmode: "driving",
            dir_action: "navigate",
        });

        const medju = zaNavigaciju.slice(1, -1).slice(0, MAX_MEDJUTACAKA);
        if (medju.length) parametri.set("waypoints", medju.map(kord).join("|"));
        if (izbegavajPutarine) parametri.set("avoid", "tolls");

        return `${GOOGLE_DIR}?${parametri.toString()}`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [potpisTacaka, izbegavajPutarine]);

    const kopirajLink = async () => {
        if (!googleLink) return;
        try {
            await navigator.clipboard.writeText(googleLink);
            setLinkKopiran(true);
            setTimeout(() => setLinkKopiran(false), 2500);
        } catch (e) {
            /* clipboard je dostupan samo na https ili localhost — tada ostaje ručno kopiranje */
            poruka("warn", t("common.upozorenje"), t("ruta.kopiranjeNijeUspelo"));
        }
    };

    const zaglavlje = (
        <div className="ruta-dijalog-naslov">
            <i className="pi pi-map" />
            <span>{t("ruta.naslov")}</span>
            {/* Oznake su iste kao u dropdownima (PT HR-51/26, RD HR-51/141/26) —
                sastavlja ih procedura, jer goli broj operateru ništa ne znači */}
            {nalog?.OznakaPutnog && (
                <span className="ruta-oznaka"><b>{nalog.OznakaPutnog}</b></span>
            )}
            {nalog?.OznakaRadnog && (
                <span className="ruta-oznaka"><b>{nalog.OznakaRadnog}</b></span>
            )}
        </div>
    );

    return (
        <>
            {/* Ulaz u rutu — badge broji stajališta, ne i tačke oblikovanja */}
            <div className="ruta-ulaz">
                <Button
                    type="button"
                    icon="pi pi-map"
                    label={t("ruta.mapaDugme")}
                    className="p-button-lg ruta-mapa-dugme"
                    disabled={!idWorkLog}
                    onClick={() => setOtvoren(true)}
                />
                {stajalista.length > 0 && (
                    <span className="ruta-badge">{stajalista.length}</span>
                )}
            </div>

            <Dialog
                header={zaglavlje}
                visible={otvoren}
                onHide={() => setOtvoren(false)}
                className="ruta-dijalog"
                style={{ width: "96vw", height: "92vh" }}
                contentStyle={{ display: "flex", flexDirection: "column", padding: "0.75rem 1rem 1rem" }}
                maximizable
                dismissableMask={false}
            >
                {/* Za koga i kojim vozilom se ova ruta vozi */}
                <div className="ruta-zaduzenje">
                    <div className="ruta-zaduzenje-polje">
                        <label>{t("ruta.vozac")}:</label>
                        <CustomDropdown
                            style={{ width: "100%" }}
                            options={vozaciDB}
                            value={rutaVozac}
                            optionLabel="label"
                            placeholder={t("ruta.phVozac")}
                            onChange={(e) => setRutaVozac(e.target.value)}
                        />
                    </div>
                    <div className="ruta-zaduzenje-polje">
                        <label>{t("ruta.vozilo")}:</label>
                        <CustomDropdown
                            style={{ width: "100%" }}
                            options={vozilaDB}
                            value={rutaVozilo}
                            optionLabel="label"
                            placeholder={t("ruta.phVozilo")}
                            onChange={(e) => setRutaVozilo(e.target.value)}
                        />
                    </div>

                    {nalog && (
                        <div className="ruta-status">
                            <span><b>{t("ruta.statusVozaca")}:</b> {nalog.StatusVozacOpis || t("ruta.nemaStatusa")}</span>
                            <span><b>{t("ruta.statusRute")}:</b> {nalog.StatusRuteOpis || t("ruta.nemaStatusa")}</span>
                            {nalog.kretanje_pocetak && (
                                <span><b>{t("ruta.krenuo")}:</b> {new Date(nalog.kretanje_pocetak).toLocaleString("sr-RS")}</span>
                            )}
                            {/* Po kom profilu je ruta računata — bez ovoga dispečer ne zna
                                da li je putanja uopšte provozna za šleper */}
                            {/* Opasan teret menja rutu, pa mora i da se VIDI da je u
                                računu. Bez ovoga dispečer dobije dužu rutu i ne zna
                                zašto — mereno, razlika ume da bude preko sto kilometara.

                                Stoji uz znak profila, a ne kao zasebna poruka: i jedno i
                                drugo odgovara na isto pitanje — po čemu je ruta računata. */}
                            {profil?.adr && (
                                <span className="ruta-profil ruta-profil-adr"
                                      title={t("ruta.adrOpis")}>
                                    <i className="pi pi-exclamation-triangle" /> {t("ruta.adr")}
                                </span>
                            )}

                            {profil?.vrsta === "KAMION" ? (
                                <span className={profil.masaZanemarena
                                        ? "ruta-profil ruta-profil-masa"
                                        : "ruta-profil ruta-profil-kamion"}
                                    title={profil.masaZanemarena
                                        ? t("ruta.masaZanemarenaOpis")
                                        : t("ruta.profilKamionOpis")}>
                                    <i className="pi pi-truck" /> {t("ruta.profilKamion")}
                                    {profil.masaZanemarena && (
                                        <b className="ruta-profil-znak">
                                            <i className="pi pi-exclamation-triangle" /> {t("ruta.masaZanemarena")}
                                        </b>
                                    )}
                                    {profil.ogranicenja && (
                                        <em>
                                            {[
                                                profil.ogranicenja.masaT && `${profil.ogranicenja.masaT} t`,
                                                profil.ogranicenja.visinaM && `${profil.ogranicenja.visinaM} m`,
                                                profil.ogranicenja.duzinaM && `${profil.ogranicenja.duzinaM} m`,
                                                profil.ogranicenja.osovine && `${profil.ogranicenja.osovine} os.`,
                                            ].filter(Boolean).join(" · ")}
                                        </em>
                                    )}
                                </span>
                            ) : null}
                            {nalog.kretanje_kraj && (
                                <span><b>{t("ruta.zavrsio")}:</b> {new Date(nalog.kretanje_kraj).toLocaleString("sr-RS")}</span>
                            )}
                        </div>
                    )}
                </div>

                <div className="ruta-alatke">
                    {/* Pretraga mesta stoji u istoj traci sa dugmadima — mapi treba
                        svaki piksel visine. */}
                    <span className="p-input-icon-left" style={{ flex: "0 1 300px", maxWidth: "300px" }}>
                        <i className="pi pi-search" />
                        <InputText
                            value={pretraga}
                            onChange={(e) => setPretraga(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") pretraziMesto(); }}
                            placeholder={t("ruta.pretragaMesta")}
                            style={{ width: "100%", paddingLeft: "2rem" }}
                        />
                    </span>
                    <Button type="button" icon={trazi ? "pi pi-spin pi-spinner" : "pi pi-search"}
                        label={t("ruta.trazi")} className="p-button-sm"
                        style={{ width: "fit-content" }}
                        disabled={trazi || pretraga.trim().length < 3}
                        onClick={pretraziMesto} />
                    {!!rezultati.length && (
                        <Button type="button" icon="pi pi-times" className="p-button-sm p-button-text"
                            style={{ width: "fit-content" }}
                            onClick={() => { setRezultati([]); setPretraga(""); }} />
                    )}

                    {/* Predlog zamenjuje ceo skup tačaka, pa je zaključan čim ruta
                        ima ijednu tačku — inače bi jednim klikom obrisao rad
                        koji je operater već uložio. */}
                    <span className="ruta-predlog-omot"
                        title={tacke.length ? t("ruta.predlogZakljucan") : undefined}>
                        <Button type="button" icon="pi pi-directions" label={t("ruta.predlogDugme")}
                            className="p-button-sm" style={{ width: "fit-content" }}
                            disabled={!predlog.length || ucitava || tacke.length > 0}
                            onClick={ucitajPredlog} />
                    </span>
                    <Button type="button" icon="pi pi-save" label={t("ruta.sacuvaj")}
                        className="p-button-sm p-button-success" style={{ width: "fit-content" }}
                        disabled={!idWorkLog} onClick={sacuvaj} />

                    <Button type="button" icon="pi pi-external-link"
                        label={t("ruta.otvoriUGoogle")}
                        className="p-button-sm p-button-secondary" style={{ width: "fit-content" }}
                        disabled={!googleLink}
                        onClick={() => googleLink && window.open(googleLink, "_blank", "noopener")} />

                    <Button type="button" icon={linkKopiran ? "pi pi-check" : "pi pi-copy"}
                        label={linkKopiran ? t("ruta.linkKopiran") : t("ruta.kopirajLink")}
                        className="p-button-sm p-button-text" style={{ width: "fit-content" }}
                        disabled={!googleLink}
                        onClick={kopirajLink} />

                    <label className="ruta-kvacica">
                        <input type="checkbox" checked={izbegavajPutarine}
                            onChange={(e) => setIzbegavajPutarine(e.target.checked)} />
                        {t("ruta.izbegavajPutarine")}
                    </label>

                    {racuna ? (
                        <span className="ruta-sumarno ruta-racuna">
                            <i className="pi pi-spin pi-spinner" /> {t("ruta.racunaSe")}
                        </span>
                    ) : sumarno ? (
                        <span className="ruta-sumarno">
                            {sumarno.km} km · {Math.floor(sumarno.minuta / 60)}h {sumarno.minuta % 60}min

                            {/* Vreme po trenutnom saobraćaju stoji PORED vremena po planu,
                                ne umesto njega. To su dve različite mere: plan je ono po
                                čemu se tura računa i ne menja se tokom dana, a saobraćaj je
                                ono što vozač zatiče sada. Prepisivanje jednog drugim
                                sakrilo bi da se razlikuju — a razlika je baš podatak.

                                Kašnjenje se ističe samo kad postoji: „+0 min" je šum. */}
                            {saSaobracajem && (
                                <span className={`ruta-saobracaj${saSaobracajem.kasnjenjeMin > 0 ? " ruta-saobracaj-kasni" : ""}`}
                                      title={t("ruta.saSaobracajemOpis")}>
                                    <i className="pi pi-clock" />
                                    {t("ruta.saSaobracajem")}:{" "}
                                    {Math.floor(saSaobracajem.minuta / 60)}h {saSaobracajem.minuta % 60}min
                                    {saSaobracajem.kasnjenjeMin > 0 && (
                                        <b>{" "}(+{saSaobracajem.kasnjenjeMin} min)</b>
                                    )}
                                </span>
                            )}

                            {prepreekeUcitava && (
                                <span className="ruta-prepreke-ceka">
                                    <i className="pi pi-spin pi-spinner" /> {t("ruta.preprekeUcitava")}
                                </span>
                            )}

                            {/* Zatvoren put se javlja PRE broja radova: to je jedina stavka
                                posle koje ruta možda uopšte ne važi. */}
                            {!prepreekeUcitava && !!sazetakPrepreka.ukupno && (
                                <span className="ruta-prepreke-znak">
                                    {!!sazetakPrepreka.zatvorenih && (
                                        <span className="ruta-znak ruta-znak-crveno">
                                            <i className="pi pi-ban" /> {sazetakPrepreka.zatvorenih}
                                        </span>
                                    )}
                                    {!!sazetakPrepreka.radova && (
                                        <span className="ruta-znak ruta-znak-narandzasto">
                                            <i className="pi pi-wrench" /> {sazetakPrepreka.radova}
                                        </span>
                                    )}
                                    <span className="ruta-znak">
                                        {t("ruta.naTrasiBroj", { broj: sazetakPrepreka.ukupno })}
                                    </span>
                                </span>
                            )}
                        </span>
                    ) : saKoordinatama.length > 1 && (
                        <span className="ruta-sumarno ruta-nedostupno">{t("ruta.nedostupno")}</span>
                    )}


                </div>

                {/* Link nosi najviše 9 međutačaka — sačuvana ruta nema to ograničenje */}
                {izostavljeno > 0 && (
                    <div className="ruta-upozorenje">
                        <i className="pi pi-exclamation-triangle" />
                        <span>
                            {t("ruta.linkOgranicenje", {
                                maks: MAX_MEDJUTACAKA,
                                izostavljeno,
                                ukupno: zaNavigaciju.length,
                            })}
                        </span>
                    </div>
                )}


                {!!rezultati.length && (
                    <div className="ruta-rezultati">
                        {rezultati.map((r, i) => (
                            <div className="ruta-rezultat" key={i}>
                                <button type="button" className="ruta-rezultat-naziv"
                                    title={t("ruta.prikaziNaMapi")}
                                    onClick={() => setCilj(r)}>
                                    <i className="pi pi-map-marker" /> {r.naziv}
                                </button>
                                <Button type="button" icon="pi pi-plus" label={t("ruta.dodajKaoStajaliste")}
                                    className="p-button-sm p-button-text"
                                    style={{ width: "fit-content" }}
                                    onClick={() => {
                                        dodajTacku(r.lat, r.lon);
                                        setCilj(r);
                                        setRezultati([]);
                                        setPretraga("");
                                    }} />
                            </div>
                        ))}
                    </div>
                )}

                <p className="ruta-uputstvo">
                    {t("ruta.uputstvo")}
                </p>

                <div className="ruta-sadrzaj">
                    <div className="ruta-mapa">
                        {/* Alatke stoje NAD mapom, a ne u traci iznad nje: biraju se uz
                            pogled na mapu i menjaju baš ono što se gleda. */}
                        <div className="ruta-alatke">
                            <button
                                type="button"
                                className={`ruta-alatka${izbornikSlojeva ? " ruta-alatka-aktivna" : ""}`}
                                onClick={() => setIzbornikSlojeva(o => !o)}
                                title={t("ruta.slojIzaberi")}
                                aria-label={t("ruta.slojIzaberi")}
                            >
                                <i className={aktivnaPodloga.ikona} />
                            </button>

                            {/* Kad provajder nije podešen, dugme STOJI ali je ugašeno:
                                sakriveno bi značilo „ne postoji", a ovako se vidi da
                                postoji i šta mu fali. */}
                            <button
                                type="button"
                                disabled={!imaSaobracaj(plocica)}
                                className={`ruta-alatka${prikaziSaobracaj && imaSaobracaj(plocica) ? " ruta-alatka-aktivna" : ""}`}
                                onClick={() => setPrikaziSaobracaj(o => !o)}
                                title={imaSaobracaj(plocica)
                                    ? t(prikaziSaobracaj ? "ruta.saobracajIskljuci" : "ruta.saobracajUkljuci")
                                    : t("ruta.saobracajNijePodesen")}
                                aria-label={t("ruta.saobracajUkljuci")}
                            >
                                <i className="pi pi-car" />
                            </button>
                        </div>

                        {izbornikSlojeva && (
                            <div className="ruta-slojevi" role="menu">
                                {SLOJEVI.map(sl => (
                                    <button
                                        key={sl.kljuc}
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={sl.kljuc === sloj}
                                        className={`ruta-sloj${sl.kljuc === sloj ? " ruta-sloj-aktivan" : ""}`}
                                        onClick={() => { setSloj(sl.kljuc); setIzbornikSlojeva(false); }}
                                    >
                                        <i className={sl.ikona} />
                                        <span className="ruta-sloj-tekst">
                                            <span className="ruta-sloj-naziv">{t(sl.naziv)}</span>
                                            <span className="ruta-sloj-opis">{t(sl.opis)}</span>
                                        </span>
                                        {sl.kljuc === sloj && <i className="pi pi-check" />}
                                    </button>
                                ))}
                            </div>
                        )}

                        <MapContainer attributionControl={false} center={CENTAR_SRBIJA} zoom={6} style={{ height: "100%", width: "100%" }} ref={mapaRef}>
                            {/* Ključ je oznaka podloge: `url` se u react-leaflet-u menja
                                u letu, ali `maxZoom` ne — bez remontiranja bi satelit
                                nasledio granicu zuma prethodne podloge. */}
                            <TileLayer
                                key={aktivnaPodloga.kljuc}
                                url={aktivnaPodloga.url}
                                maxZoom={aktivnaPodloga.maxZoom}
                            />

                            {/* Gustina je PROZIRAN sloj iznad podloge, ne zamena za nju —
                                mora da se vidi i put i koliko se na njemu stoji. Providnost
                                0.9 da se ispod obojene linije još nazire naziv ulice.

                                `updateWhenIdle` i `maxNativeZoom` čuvaju pozive ka TomTom-u:
                                pločice se ne povlače kroz prevlak nego tek kad stane, a iznad
                                zuma 13 se uvećava poslednja povučena umesto da se traže nove.
                                Ista podešavanja kao na GPS mapi — isti sloj, isti račun. */}
                            {prikaziSaobracaj && imaSaobracaj(plocica) && (
                                <TileLayer
                                    key="saobracaj"
                                    url={plocica.url}
                                    opacity={0.9}
                                    updateWhenIdle
                                    updateWhenZooming={false}
                                    maxNativeZoom={13}
                                />
                            )}
                            <KlikNaMapu aktivno={!vucenje} onKlik={(lat, lon) => dodajTacku(lat, lon)} />
                            <UokviriTacke tacke={saKoordinatama} />
                            <IdiNa cilj={cilj} />
                            <PovlacenjeLinije vucenje={vucenje} setVucenje={setVucenje} onSpusti={ubaciOblikovanje} />

                            {tacke.map((tacka, i) => (tacka.lat == null || tacka.lon == null) ? null : (
                                <Marker
                                    key={i}
                                    position={[tacka.lat, tacka.lon]}
                                    icon={tacka.oblikovanje
                                        ? rucicaIkona()
                                        : pinIkona(brojStajalista(i), izgledZaTip(tacka.tip_tacke))}
                                    draggable
                                    eventHandlers={{
                                        dragend: async (e) => {
                                            const { lat, lng } = e.target.getLatLng();
                                            let k = { lat, lon: lng };
                                            try {
                                                const [nl] = await nalepi([{ lat, lon: lng }]);
                                                if (nl && nl.udaljenost != null) k = { lat: nl.lat, lon: nl.lon };
                                            } catch (err) { /* ostaje gde je spušten */ }
                                            setTacke(prev => prev.map((t, idx) =>
                                                idx === i ? { ...t, lat: k.lat, lon: k.lon } : t));
                                        },
                                    }}
                                />
                            ))}

                            {/* Svaka deonica je zasebna linija — zato se zna između kojih
                                tačaka je operater uhvatio rutu kad je povuče */}
                            {delovi.length > 0
                                ? delovi.map((deo, i) => (
                                    <Polyline
                                        key={i}
                                        positions={deo.geometrija}
                                        pathOptions={{ color: "#1976d2", weight: 6, opacity: 0.9 }}
                                        eventHandlers={{
                                            mousedown: (e) => {
                                                L.DomEvent.stop(e);
                                                setVucenje({ deo: i, pozicija: [e.latlng.lat, e.latlng.lng] });
                                            },
                                        }}
                                    />
                                ))
                                : praveLinije.length > 1 && (
                                    <Polyline positions={praveLinije}
                                        pathOptions={{ color: "#888", weight: 3, dashArray: "6 8" }} />
                                )}

                            {/* Rezultat pretrage — vizuelna oznaka, nije deo rute */}
                            {cilj && (
                                <Marker position={[cilj.lat, cilj.lon]} icon={ikonaPretrage()}
                                    eventHandlers={{ click: () => setCilj(null) }} />
                            )}

                            {/* Radovi, blokade i zastoji NA TRASI — ne u okviru oko nje.
                                Stoje iznad linije rute, jer je pitanje „šta me čeka na
                                ovom putu", a ne „kuda put ide". */}
                            {prepreke.map(inc => (
                                <Marker
                                    key={`prep-${inc.id}`}
                                    position={[inc.lat, inc.lng]}
                                    icon={ikonaIncidenta(inc.vrsta, inc.zatvoren, pulsirajuBlokade)}
                                    zIndexOffset={300}
                                    eventHandlers={{
                                        /* Osnovni podaci na prelazak, detalji na klik. */
                                        mouseover: (e) => veziOblacic(e.target, inc, t),
                                        click: () => setOtvorenaPrepreka({
                                            incident: inc,
                                            kljuc: ++brojacPrepreke.current,
                                        }),
                                    }}
                                />
                            ))}

                            {/* Deonica u boji otvorene oznake — vidi GPS mapu. */}
                            {otvorenaPrepreka?.incident?.linija?.length > 1 &&
                                TRAGOVI_DEONICE(vrstaOpis(otvorenaPrepreka.incident.vrsta).boja).map((stil, i) => (
                                    <Polyline
                                        key={`deon-${otvorenaPrepreka.kljuc}-${i}`}
                                        positions={otvorenaPrepreka.incident.linija}
                                        pathOptions={stil}
                                    />
                                ))}

                            {otvorenaPrepreka && (
                                <Marker
                                    key={`prepk-${otvorenaPrepreka.kljuc}`}
                                    position={[otvorenaPrepreka.incident.lat, otvorenaPrepreka.incident.lng]}
                                    icon={ikonaIncidenta(otvorenaPrepreka.incident.vrsta,
                                                         otvorenaPrepreka.incident.zatvoren,
                                                         pulsirajuBlokade)}
                                    zIndexOffset={350}
                                    ref={(m) => { if (m) m.openPopup(); }}
                                >
                                    <Popup
                                        className="gps-pin-popup"
                                        minWidth={300}
                                        maxWidth={340}
                                        autoPan
                                        autoPanPadding={[24, 60]}
                                        closeButton
                                        /* Čišćenje samo ako u međuvremenu nije otvorena NOVA
                                            kartica — Leaflet `remove` javlja i kad staru
                                            zatvara zato što je nova zamenjuje. */
                                        eventHandlers={{
                                            remove: () => setOtvorenaPrepreka(
                                                (x) => (x && x.kljuc === otvorenaPrepreka.kljuc ? null : x)
                                            ),
                                        }}
                                    >
                                        <PopupIncident incident={otvorenaPrepreka.incident} />
                                    </Popup>
                                </Marker>
                            )}

                            {/* Nagoveštaj gde će nova tačka pasti dok se linija vuče */}
                            {vucenje && (
                                <Marker position={vucenje.pozicija} icon={rucicaIkona(true)} />
                            )}
                        </MapContainer>
                    </div>

                    <div className="ruta-tacke">
                        {/* Spisak stoji IZNAD tačaka rute: poređan je po tome koliko je
                            daleko duž trase, pa se čita kao redosled onoga što vozača
                            čeka — isti smer u kom se i tačke rute ređaju. */}
                        {!!prepreke.length && (
                            <div className="ruta-prepreke">
                                <div className="ruta-prepreke-naslov">{t("ruta.naTrasi")}</div>
                                {prepreke.map(inc => {
                                    const o = vrstaOpis(inc.vrsta);
                                    const kasni = kasnjenjeTeksta(inc.kasnjenje);
                                    return (
                                        <button
                                            key={`sp-${inc.id}`}
                                            type="button"
                                            className="ruta-prepreka"
                                            onClick={() => {
                                                setOtvorenaPrepreka({ incident: inc, kljuc: ++brojacPrepreke.current });
                                                mapaRef.current?.flyTo([inc.lat, inc.lng], 12, { duration: 0.6 });
                                            }}
                                        >
                                            <span className="ruta-prepreka-km">
                                                {inc.kmOdPocetka != null ? `${inc.kmOdPocetka} km` : ""}
                                            </span>
                                            <i className={o.ikona} style={{ color: o.boja }} />
                                            <span className="ruta-prepreka-tekst">
                                                <span className="ruta-prepreka-vrsta" style={{ color: o.boja }}>
                                                    {t(o.naziv)}
                                                </span>
                                                {!!inc.naslov && <span className="ruta-prepreka-gde">{inc.naslov}</span>}
                                            </span>
                                            {!!kasni && <span className="ruta-prepreka-kasni">+{kasni}</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {!tacke.length && <div className="ruta-prazno">{t("ruta.nemaTacaka")}</div>}

                        {/* Oblikovanja se čiste sva odjednom; pojedinačno se briše
                            iz svog reda u spisku */}
                        {brojOblikovanja > 0 && (
                            <div className="ruta-oblikovanja-traka">
                                <span>{t("ruta.oblikovanjaNaslov")} ({brojOblikovanja})</span>
                                <Button type="button" icon="pi pi-trash"
                                    label={t("ruta.obrisiOblikovanja")}
                                    className="p-button-text p-button-sm p-button-danger"
                                    onClick={obrisiSvaOblikovanja} />
                            </div>
                        )}

                        {/* Jedan spisak, u redosledu obilaska — kao na velikim mapama.
                            Stajališta nose broj, oblikovanja su tanji red bez broja,
                            ali stoje tačno tamo gde su i na mapi. */}
                        {tacke.map((tacka, i) => {
                            if (tacka.oblikovanje) {
                                return (
                                    <div className="ruta-oblikovanje-red" key={i}>
                                        <span className="ruta-rucica-mini" />
                                        <div className="ruta-oblikovanje-koord">
                                            <KoordinateUnos
                                                lat={tacka.lat}
                                                lon={tacka.lon}
                                                onPromeni={(lat, lon) =>
                                                    setTacke(prev => prev.map((x, idx) =>
                                                        idx === i ? { ...x, lat, lon } : x))
                                                }
                                            />
                                        </div>
                                        <Button type="button" icon="pi pi-arrow-up" className="p-button-text p-button-sm"
                                            disabled={i === 0} onClick={() => pomeri(i, -1)} />
                                        <Button type="button" icon="pi pi-arrow-down" className="p-button-text p-button-sm"
                                            disabled={i === tacke.length - 1} onClick={() => pomeri(i, 1)} />
                                        <Button type="button" icon="pi pi-flag"
                                            className="p-button-text p-button-sm"
                                            tooltip={t("ruta.uStajaliste")}
                                            tooltipOptions={{ position: "top" }}
                                            onClick={() => uStajaliste(i)} />
                                        <Button type="button" icon="pi pi-times"
                                            className="p-button-text p-button-danger p-button-sm"
                                            onClick={() => obrisiTacku(i)} />
                                    </div>
                                );
                            }

                            const izgled = izgledZaTip(tacka.tip_tacke);
                            return (
                                <div className="ruta-tacka" key={i}>
                                    <div className="ruta-tacka-red">
                                        <span className="ruta-broj" style={{ background: izgled.boja }}>
                                            {brojStajalista(i)}
                                        </span>
                                        <div className="ruta-tip">
                                            <CustomDropdown
                                                style={{ width: "100%" }}
                                                options={tipoviTacke}
                                                value={tacka.tip_tacke}
                                                optionLabel="label"
                                                onChange={(e) => izmeniTacku(i, "tip_tacke", e.target.value)}
                                                placeholder={t("ruta.tipTacke")}
                                            />
                                        </div>
                                        <Button type="button" icon="pi pi-arrow-up" className="p-button-text p-button-sm"
                                            disabled={i === 0} onClick={() => pomeri(i, -1)} />
                                        <Button type="button" icon="pi pi-arrow-down" className="p-button-text p-button-sm"
                                            disabled={i === tacke.length - 1} onClick={() => pomeri(i, 1)} />
                                        <Button type="button" icon="pi pi-plus" className="p-button-text p-button-sm"
                                            tooltip={t("ruta.dodajPosle")} tooltipOptions={{ position: "top" }}
                                            onClick={() => dodajStajaliste(i)} />
                                        <Button type="button" icon="pi pi-times" className="p-button-text p-button-danger p-button-sm"
                                            onClick={() => obrisiTacku(i)} />
                                    </div>

                                    {/* Naziv i napomena dele red, kao Lat i Lon ispod —
                                        kartica ostaje niska pa staje više tačaka na ekran.
                                        Placeholder je izostavljen jer bi se u polju
                                        na pola širine ionako presekao. */}
                                    <div className="ruta-tekst-red">
                                        <div className="ruta-polje">
                                            <label className="ruta-labela">{t("ruta.nazivTacke")}</label>
                                            <InputText value={tacka.naziv}
                                                onChange={(e) => izmeniTacku(i, "naziv", e.target.value)} />
                                        </div>
                                        <div className="ruta-polje">
                                            <label className="ruta-labela">{t("ruta.napomenaTacke")}</label>
                                            <InputText value={tacka.napomena}
                                                onChange={(e) => izmeniTacku(i, "napomena", e.target.value)} />
                                        </div>
                                    </div>

                                    <KoordinateUnos
                                        lat={tacka.lat}
                                        lon={tacka.lon}
                                        onPromeni={(lat, lon) =>
                                            setTacke(prev => prev.map((x, idx) =>
                                                idx === i ? { ...x, lat, lon } : x))
                                        }
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default RutaVozaca;
